import React, { useEffect, useState } from "react";
import api from "../../api/axiosConfig";
import ReactECharts from "echarts-for-react";
import {
  FaMoneyBillWave,
  FaFileInvoiceDollar,
  FaClipboardList,
  FaFilter,
} from "react-icons/fa";

function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [cargandoFiltros, setCargandoFiltros] = useState(true);
  const [error, setError] = useState(null);

  // Resumen que viene del backend
  const [resumen, setResumen] = useState({
    cantidadPedidos: 0,
    cantidadFacturas: 0,
    totalFacturado: 0,
  });

  const [ventasUltimos7Dias, setVentasUltimos7Dias] = useState([]);
  const [productosMasVendidos, setProductosMasVendidos] = useState([]);

  // Filtros
  const hoy = new Date().toISOString().slice(0, 10);
  const [fecha, setFecha] = useState(hoy);

  // Usuario (filtro solo por nombre)
  const [usuarios, setUsuarios] = useState([]);
  const [usuarioTexto, setUsuarioTexto] = useState("");
  const [usuarioId, setUsuarioId] = useState("");
  const [mostrarResultados, setMostrarResultados] = useState(false);

  // Mesa
  const [mesaNumero, setMesaNumero] = useState("");
  const [mesas, setMesas] = useState([]);

  // Horas
  const [horaInicio, setHoraInicio] = useState("");
  const [horaFin, setHoraFin] = useState("");

  // Cargar usuarios y mesas
  useEffect(() => {
    const cargarFiltros = async () => {
      try {
        setCargandoFiltros(true);
        setError(null);

        const [resUsuarios, resMesas] = await Promise.all([
          api.get("/usuarios"),
          api.get("/mesas"),
        ]);

        setUsuarios(resUsuarios.data || []);
        setMesas(resMesas.data || []);
      } catch (err) {
        console.error(err);
        setError(
          "No se pudieron cargar correctamente los filtros (usuarios/mesas)."
        );
      } finally {
        setCargandoFiltros(false);
      }
    };

    cargarFiltros();
  }, []);

  // Cerrar lista de resultados al hacer click fuera
  useEffect(() => {
    const handleClickOutside = () => setMostrarResultados(false);
    window.addEventListener("click", handleClickOutside);
    return () => window.removeEventListener("click", handleClickOutside);
  }, []);

  // Cargar resumen del dashboard según filtros
  const cargarDashboard = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = { fecha }; // obligatorio

      // Enviamos usuarioId como meseroId al backend
      if (usuarioId) params.meseroId = usuarioId;
      if (mesaNumero) params.mesaNumero = mesaNumero;
      if (horaInicio) params.horaInicio = horaInicio; // "HH:mm"
      if (horaFin) params.horaFin = horaFin;

      const res = await api.get("/dashboard/resumen", { params });
      const data = res.data;

      setResumen({
        cantidadPedidos: data.cantidadPedidos ?? 0,
        cantidadFacturas: data.cantidadFacturas ?? 0,
        totalFacturado: data.totalFacturado ?? 0,
      });

      setVentasUltimos7Dias(data.ventasUltimos7Dias ?? []);

      // Top 5 productos más vendidos
      setProductosMasVendidos((data.productosMasVendidos ?? []).slice(0, 5));
    } catch (err) {
      console.error(err);
      setError("No se pudo cargar el dashboard con datos reales.");
    } finally {
      setLoading(false);
    }
  };

  // Cargar al arrancar
  useEffect(() => {
    cargarDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filtrado simple por nombre de usuario
  const usuariosFiltrados = usuarios.filter((u) =>
    u.nombre?.toLowerCase().includes(usuarioTexto.toLowerCase())
  );

  // ───────────── OPCIONES ECHARTS ─────────────

  const opcionesVentasUltimos7Dias = {
    title: {
      text: "Ventas últimos 7 días",
      left: "center",
    },
    tooltip: { trigger: "axis" },
    xAxis: {
      type: "category",
      data: ventasUltimos7Dias.map((v) => v.fecha),
    },
    yAxis: {
      type: "value",
      name: "Ventas ($)",
    },
    toolbox: {
      show: true,
      orient: "vertical",
      left: "right",
      top: "center",
      feature: {
        mark: { show: true },
        dataView: { show: true, readOnly: false },
        magicType: { show: true, type: ["line", "bar"] },
        saveAsImage: { show: true },
      },
    },
    series: [
      {
        name: "Ventas",
        type: "bar",
        data: ventasUltimos7Dias.map((v) => v.total),
      },
    ],
  };

  const opcionesProductosMasVendidos = {
    title: {
      text: "Top 5 productos más vendidos",
      left: "center",
    },
    tooltip: { trigger: "axis" },
    xAxis: {
      type: "category",
      data: productosMasVendidos.map((p) => p.nombreProducto),
      axisLabel: { rotate: 30, interval: 0 },
    },
    yAxis: {
      type: "value",
      name: "Cantidad",
    },
    toolbox: {
      show: true,
      orient: "vertical",
      left: "right",
      top: "center",
      feature: {
        mark: { show: true },
        dataView: { show: true, readOnly: false },
        magicType: { show: true, type: ["line", "bar"] },
        saveAsImage: { show: true },
      },
    },
    series: [
      {
        name: "Cantidad",
        type: "bar",
        data: productosMasVendidos.map((p) => p.cantidad),
      },
    ],
  };

  // ───────────── RENDER ─────────────

  if (
    loading &&
    !resumen.cantidadPedidos &&
    !resumen.cantidadFacturas &&
    !resumen.totalFacturado
  ) {
    return (
      <div className="flex items-center justify-center h-[70vh] text-slate-700 dark:text-slate-100">
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm">Cargando dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 px-3 md:px-6">
      <h3 className="text-lg font-semibold text-amber-500 dark:text-amber-300">
        Dashboard
      </h3>

      {error && (
        <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-sm px-3 py-2">
          {error}
        </div>
      )}

      {/* Filtros */}
      <div className="mb-4 bg-white/95 dark:bg-slate-900/90 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-sm p-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            cargarDashboard();
          }}
        >
          <div className="flex items-center gap-2 mb-3">
            <FaFilter className="text-slate-400" />
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              Filtros
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 gap-3">
            {/* Fecha */}
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                Fecha
              </label>
              <input
                type="date"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/80 px-3 py-2 text-xs sm:text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent"
              />
            </div>

            {/* Usuario por nombre (autocompletar) */}
            <div
              className="relative"
              onClick={(e) => e.stopPropagation()}
            >
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                Usuario que atendió
              </label>
              <input
                type="text"
                placeholder="Escribe un nombre..."
                value={usuarioTexto}
                onChange={(e) => {
                  setUsuarioTexto(e.target.value);
                  setMostrarResultados(true);
                  setUsuarioId("");
                }}
                autoComplete="off"
                disabled={cargandoFiltros}
                onClick={(e) => {
                  e.stopPropagation();
                  setMostrarResultados(true);
                }}
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/80 px-3 py-2 text-xs sm:text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent"
              />

              {mostrarResultados && usuarioTexto.length > 0 && (
                <div className="absolute z-20 mt-1 w-full max-h-40 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg text-xs text-slate-800">
                  {usuariosFiltrados.length === 0 && (
                    <div className="px-3 py-2 text-slate-400">
                      Sin coincidencias
                    </div>
                  )}
                  {usuariosFiltrados.map((u) => (
                    <div
                      key={u.id}
                      className="px-3 py-1.5 hover:bg-slate-100 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        setUsuarioTexto(u.nombre);
                        setUsuarioId(u.id);
                        setMostrarResultados(false);
                      }}
                    >
                      {u.nombre}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Mesa */}
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                Mesa
              </label>
              <select
                value={mesaNumero}
                onChange={(e) => setMesaNumero(e.target.value)}
                disabled={cargandoFiltros}
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/80 px-3 py-2 text-xs sm:text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent"
              >
                <option value="">Todas</option>
                {mesas.map((m) => (
                  <option key={m.numero} value={m.numero}>
                    Mesa {m.numero}
                  </option>
                ))}
              </select>
            </div>

            {/* Hora inicio */}
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                Hora inicio
              </label>
              <input
                type="time"
                value={horaInicio}
                onChange={(e) => setHoraInicio(e.target.value)}
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/80 px-3 py-2 text-xs sm:text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent"
              />
            </div>

            {/* Hora fin */}
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                Hora fin
              </label>
              <input
                type="time"
                value={horaFin}
                onChange={(e) => setHoraFin(e.target.value)}
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/80 px-3 py-2 text-xs sm:text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent"
              />
            </div>

            {/* Botón aplicar */}
            <div className="flex items-end">
              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-xs sm:text-sm font-medium px-3 py-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? "Cargando..." : "Aplicar filtros"}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Tarjetas resumen */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mb-4">
        <div className="bg-white/95 dark:bg-slate-900/90 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-sm p-4 flex items-center gap-3">
          <div className="p-3 rounded-xl bg-emerald-500/10 text-emerald-400">
            <FaMoneyBillWave className="w-6 h-6" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Total facturado ({fecha})
            </div>
            <div className="text-lg font-semibold text-slate-800 dark:text-slate-100">
              ${resumen.totalFacturado.toLocaleString("es-CO")}
            </div>
          </div>
        </div>

        <div className="bg-white/95 dark:bg-slate-900/90 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-sm p-4 flex items-center gap-3">
          <div className="p-3 rounded-xl bg-sky-500/10 text-sky-400">
            <FaFileInvoiceDollar className="w-6 h-6" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Facturas emitidas ({fecha})
            </div>
            <div className="text-lg font-semibold text-slate-800 dark:text-slate-100">
              {resumen.cantidadFacturas}
            </div>
          </div>
        </div>

        <div className="bg-white/95 dark:bg-slate-900/90 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-sm p-4 flex items-center gap-3">
          <div className="p-3 rounded-xl bg-amber-500/10 text-amber-400">
            <FaClipboardList className="w-6 h-6" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Pedidos tomados ({fecha})
            </div>
            <div className="text-lg font-semibold text-slate-800 dark:text-slate-100">
              {resumen.cantidadPedidos}
            </div>
          </div>
        </div>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-white/95 dark:bg-slate-900/90 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-sm p-3">
          <ReactECharts
            option={opcionesVentasUltimos7Dias}
            style={{ height: "320px", width: "100%" }}
          />
        </div>

        <div className="bg-white/95 dark:bg-slate-900/90 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-sm p-3">
          <ReactECharts
            option={opcionesProductosMasVendidos}
            style={{ height: "320px", width: "100%" }}
          />
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
